import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BACKEND_URL } from '../../config';
import { Users, Layers, Wallet, Target, Building2, Globe2 } from 'lucide-react';

/**
 * Live stats strip. Pulls aggregate numbers from `/api/landing/stats`
 * (public endpoint, 60s server-side cache) and animates each number
 * counting up from 0 when the strip scrolls into view.
 *
 * Rotates through 6 metrics in 4 visible tile slots, cycling every 5s
 * with a smooth cross-fade so every number gets time in the spotlight
 * without crowding the hero.
 */
const StatsStrip = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/landing/stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats({
        certified_actors: 0, active_investments: 0, total_budget_eur: 0,
        beneficiaries_reached: 0, organisations_onboarded: 0, sectors_covered: 0,
      }));
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } });
    }, { threshold: 0.3 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // All 6 metrics defined once. The tile slots (4 visible) cycle through these.
  const allTiles = [
    { icon: Users,     key: 'certified_actors',        format: 'int'   },
    { icon: Layers,    key: 'active_investments',      format: 'int'   },
    { icon: Wallet,    key: 'total_budget_eur',        format: 'money' },
    { icon: Target,    key: 'beneficiaries_reached',   format: 'int'   },
    { icon: Building2, key: 'organisations_onboarded', format: 'int'   },
    { icon: Globe2,    key: 'sectors_covered',         format: 'int'   },
  ];

  const slotCount = 4;
  const totalPages = allTiles.length;  // one page per metric — creates a smooth rolling window

  // Auto-advance the rolling window every 5s once the section is visible.
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setPage((p) => (p + 1) % totalPages), 5000);
    return () => clearInterval(id);
  }, [visible, totalPages]);

  // Current window of 4 tiles starting at `page`, wrapping around.
  const windowTiles = Array.from({ length: slotCount }, (_, i) => allTiles[(page + i) % allTiles.length]);

  return (
    <section
      ref={ref}
      id="stats"
      className="relative py-14 md:py-16 bg-gradient-to-r from-slate-900 via-emerald-900/95 to-teal-900 text-white"
      data-testid="landing-stats-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-emerald-300 text-xs font-semibold tracking-widest uppercase mb-2">
            {t('landing.stats.eyebrow')}
          </p>
          <h2 className="text-2xl md:text-3xl font-bold">{t('landing.stats.title')}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {windowTiles.map((tile, slotIdx) => {
            const value = stats?.[tile.key] || 0;
            const Icon = tile.icon;
            return (
              <div
                key={`${tile.key}-${slotIdx}`}
                className="text-center border-l-2 border-emerald-500/30 pl-4 md:pl-6 animate-stat-fade"
                data-testid={`stat-${tile.key}`}
              >
                <Icon className="w-6 h-6 text-emerald-300 mb-3 mx-auto" />
                <div className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
                  <CountUp value={value} run={visible} format={tile.format} locale={i18n.language} />
                </div>
                <p className="mt-2 text-xs uppercase tracking-wider text-slate-300 font-semibold">
                  {t(`landing.stats.${tile.key}`)}
                </p>
              </div>
            );
          })}
        </div>

        {/* Rotation progress dots */}
        <div className="flex justify-center gap-2 mt-8" data-testid="stats-rotation-dots">
          {allTiles.map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              aria-label={`Show stat ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === page ? 'w-6 bg-emerald-400' : 'w-1.5 bg-slate-600 hover:bg-slate-500'
              }`}
            />
          ))}
        </div>

        <p className="text-xs text-slate-500 text-center mt-4">
          {t('landing.stats.live_note')}
          {stats?.as_of && <span className="ml-1 opacity-60">· {new Date(stats.as_of).toLocaleString(i18n.language)}</span>}
        </p>
      </div>

      <style>{`
        @keyframes statFade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-stat-fade { animation: statFade 600ms ease-out both; }
      `}</style>
    </section>
  );
};

/**
 * Lightweight count-up animation — eases from 0 to `value` over ~1.2s
 * once `run` becomes true. Re-animates whenever `value` changes, so
 * rotated-in tiles count up nicely each time they appear.
 */
const CountUp = ({ value, run, format, locale }) => {
  const [n, setN] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!run) return;
    const start = performance.now();
    const duration = 1200;
    const from = 0;
    const to = Number(value) || 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);  // easeOutCubic
      setN(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, run]);

  if (format === 'money') {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return <>€{(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M</>;
    if (v >= 1_000)     return <>€{(v / 1_000).toFixed(0)}K</>;
    return <>€{Math.round(v).toLocaleString(locale)}</>;
  }
  return <>{Math.round(n).toLocaleString(locale)}</>;
};

export default StatsStrip;
